import { fromLocaleDate } from "@/helpers/dateHelper";
import { DirectionEnum, PassengerTypeEnum } from "@/models/enums";
import { dialogKey, DialogType } from "@/models/symbols/Dialog";
import { AddressInformationState } from "@/models/www/store/BookingState";
import passengerService from "@/services/passengers.service";
import { storageService } from "@/services/storage.service";
import { useBookingStore } from "@/store/booking";
import { useEventBus } from "@vueuse/core";
import { useI18n } from "vue-i18n";

export interface PassengerType {
	type: PassengerTypeEnum;
	name: string;
	ageSuffix: string;
	min?: number;
	max?: number;
}

const usePassengers = () => {
	const { t } = useI18n();
	const dialogBus = useEventBus(dialogKey);
	const bookingStore = useBookingStore();

	const allPassengerTypes = [
		{
			type: PassengerTypeEnum.ShareholderAdult,
			name: t("passenger.SHA"),
			ageSuffix: "(16YRS+ and qualified for shareholder discount)",
			min: 16
		},
		{
			type: PassengerTypeEnum.Adult,
			name: t("passenger.A"),
			ageSuffix: "(16YRS+)",
			min: 16
		},
		{
			type: PassengerTypeEnum.ShareholderYouth,
			name: t("passenger.SHY"),
			ageSuffix: "(12-15YRS and shareholder)",
			min: 12,
			max: 15
		},
		{
			type: PassengerTypeEnum.Youth,
			name: t("passenger.Y"),
			ageSuffix: "(12-15YRS)",
			min: 12,
			max: 15
		},
		{
			type: PassengerTypeEnum.ShareholderChild,
			name: t("passenger.SHC"),
			ageSuffix: "(2-11YRS and shareholder)",
			min: 2,
			max: 11
		},
		{
			type: PassengerTypeEnum.Child,
			name: t("passenger.C"),
			ageSuffix: "(2-11YRS)",
			min: 2,
			max: 11
		},
		{
			type: PassengerTypeEnum.ShareholderInfant,
			name: t("passenger.SHI"),
			ageSuffix: "(0-23 MONTHS and shareholder)",
			min: 0,
			max: 1
		},
		{
			type: PassengerTypeEnum.Infant,
			name: t("passenger.I"),
			ageSuffix: "(0-23 MONTHS)",
			min: 0,
			max: 1
		},
		{
			type: PassengerTypeEnum.Dog,
			name: t("passenger.D"),
		},
	] as PassengerType[]

	const regularPassengerTypes = [
		{
			type: PassengerTypeEnum.Adult,
			name: t("passenger.A"),
			ageSuffix: "(16YRS+)",
			min: 16
		},
		{
			type: PassengerTypeEnum.Youth,
			name: t("passenger.Y"),
			ageSuffix: "(12-15YRS)",
			min: 12,
			max: 15
		},
		{
			type: PassengerTypeEnum.Child,
			name: t("passenger.C"),
			ageSuffix: "(2-11YRS)",
			min: 2,
			max: 11
		},
		{
			type: PassengerTypeEnum.Infant,
			name: t("passenger.I"),
			ageSuffix: "(0-23 MONTHS)",
			min: 0,
			max: 1
		},
		{
			type: PassengerTypeEnum.Dog,
			name: t("passenger.D"),
		},
	] as PassengerType[]

	const shareholderPassengetTypes = [
		{
			type: PassengerTypeEnum.ShareholderAdult,
			name: t("passenger.A"),
			ageSuffix: "(16YRS+ and qualified for shareholder discount)",
			min: 16
		},
		{
			type: PassengerTypeEnum.ShareholderYouth,
			name: t("passenger.Y"),
			ageSuffix: "(12-15YRS and shareholder)",
			min: 12,
			max: 15
		},
		{
			type: PassengerTypeEnum.ShareholderChild,
			name: t("passenger.C"),
			ageSuffix: "(2-11YRS and shareholder)",
			min: 2,
			max: 11
		},
		{
			type: PassengerTypeEnum.ShareholderInfant,
			name: t("passenger.I"),
			ageSuffix: "(0-23 MONTHS and shareholder)",
			min: 0,
			max: 1
		},
	] as PassengerType[]

	const getShareholderPassengers = () => {
		return shareholderPassengetTypes;
	}

	const getRegularPassengers = () => {
		return regularPassengerTypes;
	}

	const getPassengers = () => {
		return allPassengerTypes;
	}

	const setPassengers = async () => {
		return await passengerService.setPassengers({
			leadPassengerId: bookingStore.getBooking.leadPassengerId,
			addressInformation: bookingStore.getBooking.addressInformation,
			passengers: bookingStore.getBooking.outward.passengers.map(p => ({
				...p,
				dateOfBirth: fromLocaleDate(p.dateOfBirth as string)
			}))
		});
	}

	const syncPassengers = async () => {
		return new Promise((resolve, reject) => {
			passengerService.getPassengers().then((result) => {
				if (result && result.passengers.length > 0) {
					//save logged in user data on the lead passenger
					const leadIndex = result.passengers.findIndex(x => x.nameListId === "P1");

					//also set the logged in user as default lead passenger
					bookingStore.setLeadPassenger(result.passengers[leadIndex].nameListId);
					bookingStore.setAddressInformation({
						mobilePhone: result.address.mobilePhone,
						postCode: result.address.zip,
						address: result.address.address,
						city: result.address.city,
						countryCode: result.address.countryCode,
						email: result.address.email,
					} as AddressInformationState);

					bookingStore.setPassengers(DirectionEnum.Outward, result.passengers);
					resolve("");
				} else {
					const err = "No passengers on booking, you will be redirected to the start of the booking process.";

					dialogBus.emit({
						title: t("messages.missingPassengers.title"),
						message: err,
						yes: t("button.ok"),
						callback: () => {
							storageService.clearAll();
							window.location.replace(location.pathname);
						},
						type: DialogType.Danger,
					});

					reject(err);
				}
			});
		})
	}

	return { getPassengers, 
			 setPassengers, 
			 syncPassengers,
			 getShareholderPassengers,
			 getRegularPassengers }
}

export default usePassengers;